<template>
  <div
    :class="switchKls"
    role="switch"
    :aria-checked="checked"
    :aria-disabled="(switchDisabled as boolean)"
    @click.prevent="switchValue"
  >
    <input
      :id="id"
      ref="input"
      v-bind="attrs"
      :class="ns.e('input')"
      type="checkbox"
      :true-value="trueValue"
      :false-value="falseValue"
      :disabled="(switchDisabled as boolean)"
      @change="handleChange"
      @keydown.enter="switchValue"
    />
    <span
      v-if="!inlinePrompt && ( inactiveText || activeText )"
      :class="[
        ns.e('label'),
        ns.em('label', 'left'),
        ns.is('active', !checked),
      ]"
    >
      <span :aria-hidden="!checked">{{
        inactiveText
      }}</span>
    </span>
    <span
      ref="core"
      :class="ns.e('core')"
      :style="corWidth"
    >

      <div :class="ns.e('action')">
        <sg-icon v-if="loading" :class="ns.is('loading')"><Loading /></sg-icon>
      </div>
      <slot name="open" v-if="checked"></slot>
      <slot name="close" v-if="!checked"></slot>
    </span>
    <span
      v-if="!inlinePrompt &&  activeText"
      :class="[
        ns.e('label'),
        ns.em('label', 'right'),
        ns.is('active', checked),
      ]"
    >
      <span v-if="activeText" :aria-hidden="!checked">{{
        activeText
      }}</span>
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref, nextTick, watch } from 'vue'
import { isPromise } from '@vue/shared'
import { isBool } from '@sgui-plus/utils/util'
import { throwError, debugWarn } from '@sgui-plus/utils/error'
import SgIcon from '@sgui-plus/components/icon'
import { Loading } from '@sgui-plus/icons-vue'
import {
  UPDATE_MODEL_EVENT,
  INPUT_EVENT,
} from '@sgui-plus/utils/constants'
import {
  useDisabled,
  useNamespace,
  useSize,
  useFormItem
} from '@sgui-plus/hooks'
import { switchProps, switchEmits } from './switch'

const COMPONENT_NAME = 'SgSwitch'

export default defineComponent({
  name: COMPONENT_NAME,
  components: { SgIcon, Loading },

  props: switchProps,
  emits: switchEmits,

  setup(props, { attrs, emit }) {
    const { formItem } = useFormItem()
    const switchDisabled = useDisabled(computed(() => props.loading).value)
    const corWidth = computed(() => props.width ? { width: props.width + 'px' } : undefined)
    const ns = useNamespace('switch')

    const switchSize = useSize()
    const isModelValue = ref(props.modelValue !== false)
    const input = ref<HTMLInputElement>()
    const core = ref<HTMLSpanElement>()

    const switchKls = computed(() => [
      ns.b(),
      ns.m(switchSize.value),
      ns.is('disabled', switchDisabled.value as boolean),
      ns.is('checked', checked.value),
    ])

    watch(
      () => props.modelValue,
      () => {
        isModelValue.value = true
      }
    )

    watch(
      () => props.value,
      () => {
        isModelValue.value = false
      }
    )

    const actualValue = computed(() => {
      return isModelValue.value ? props.modelValue : props.value
    })

    const checked = computed(() => actualValue.value === props.trueValue)

    if (![props.trueValue, props.falseValue].includes(actualValue.value)) {
      emit(UPDATE_MODEL_EVENT, props.falseValue)
      emit(INPUT_EVENT, props.falseValue)
    }

    watch(checked, () => {
      input.value!.checked = checked.value
      if (props.validateEvent) {
        formItem?.validate?.('change').catch((err) => debugWarn(err))
      }
    })

    const handleChange = (): void => {
      const val = checked.value ? props.falseValue : props.trueValue
      emit("onChange", val)
      emit(UPDATE_MODEL_EVENT, val)
      emit(INPUT_EVENT, val)
      nextTick(() => {
        input.value!.checked = checked.value
      })
    }

    const switchValue = (): void => {
      if (switchDisabled.value) return

      const { beforeChange } = props
      if (!beforeChange) {
        handleChange()
        return
      }

      const shouldChange = beforeChange()

      const isExpectType = [isPromise(shouldChange), isBool(shouldChange)].some(
        (i) => i
      )
      if (!isExpectType) {
        throwError(
          COMPONENT_NAME,
          'beforeChange must return type `Promise<boolean>` or `boolean`'
        )
      }

      if (isPromise(shouldChange)) {
        shouldChange
          .then((result) => {
            if (result) {
              handleChange()
            }
          })
          .catch((e) => {
            debugWarn(COMPONENT_NAME, `some error occurred: ${e}`)
          })
      } else if (shouldChange) {
        handleChange()
      }
    }

    onMounted(() => {
      input.value!.checked = checked.value
    })

    return {
      corWidth,
      attrs,
      ns,
      input,
      core,
      switchDisabled,
      checked,
      switchKls,
      handleChange,
      switchValue,
    }
  },
})
</script>
